import { Box, Button, H6, Optional } from '@honda-canada/design-system-react';
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UserLocationContext } from '@honda-canada/user-location';
import { useTranslation } from 'react-i18next';
import { createQueryParam, getTransmissionKeyFromDefaultTransmission } from '../../../utils/urls';
import { useAppName, useLanguage, useProvinces, useSettings } from '../../../utils/sitecoreContext';

import CardSpecialOfferBadge from '../../UniCard/shared/CardSpecialOfferBadge';
import { EqualHeightContext } from '../../../utils/components/EqualHeight/context';
import { EqualHeightElement } from '../../../utils/components/EqualHeight';
import PriceComponent from '../../PriceComponent';
import ProductImageNoWidth from './ProductImageNoWidth';
import ProductTitle from './ProductTitle';
import SaveLabel from '../../PriceComponent/SaveLabel';
import { getIsSellingPriceProvince } from '../../../utils/financeUtils';
import { getPricingWithLabel } from '../utils';
import themeStyles from '../styles/ProductCard.styles';
import trimPaymentFormatter from '../../TrimPackages/Details/Payment/utils';
import { useProductCompareData } from '../ProductCompareDataContextProvider';
import useProductFinancial from '../../../apiHooks/useProductFinancial';
import { useTrimPayment } from '../../../apiHooks';

const PriceButton = themeStyles.apply(Button, 'PriceButton');
const BuyNowButton = themeStyles.apply(Button, 'BuyNowButton');
const PriceButtonWrapper = themeStyles.apply(Box, 'PriceButtonWrapper');

const ProductPriceAndFinance = ({
  modelKey,
  modelYear,
  trimId,
  transmissionId,
  vehicleType,
  isSticky,
  hidePriceAndFinance,
  productCardTitle,
  thumbnail,
  productIndex,
  suppressDiscount,
  showSellingPriceDisclaimer,
  paymentOptions,
  trimKey,
  transmissionKey,
  defaultExteriorColorKey,
  wrapperGtmTags,
  ctaGtmTags,
  showBuyNowCta,
  handleBuyNowClick,
  priceSize,
  priceTitleSize,
  isVisibleOnMobile,
  productPricingFormatter,
  isDesktop,
  hasSpecialOffer,
}) => {
  const { t } = useTranslation();
  const appName = useAppName();
  const { defaultProvince } = useSettings();
  const provinces = useProvinces();
  const { setForceUpdate } = useContext(EqualHeightContext);
  const language = useLanguage();
  const shouldFetch = true;

  const { sellingPriceTooltipDiscalimerLabel } = useProductCompareData();

  const provinceCode = useContext(UserLocationContext)?.provinceCode || defaultProvince;
  const isSellingPriceProvince = getIsSellingPriceProvince(provinceCode, provinces);

  const priceComponentStyles = {
    priceStyles: {
      title: {
        size: priceTitleSize,
      },
      copy: { size: priceSize },
    },
    saveStyles: {
      container: {
        mx: 'auto',
      },
      copy: { size: 'extraSmall' },
    },
  };

  const priceLabels = {
    priceErrorLabel: t('Shared.Errors.priceError'),
    msrpStartingFromLabel: t('Shared.Common.msrpStartingFromLabel'),
    sellingPriceLabel: t('Shared.Common.sellingPriceLabel'),
  };

  const trimPayment = {
    trimKey,
    transmissionKey,
    exteriorColorKey: defaultExteriorColorKey,
    modelKey,
    modelYear,
  };

  const { payment } = useTrimPayment(
    trimPayment,
    paymentOptions,
    provinceCode,
    data => trimPaymentFormatter(appName, data, paymentOptions, language),
    shouldFetch,
    vehicleType,
    isSellingPriceProvince,
  );

  const selectedTransmissionKey = getTransmissionKeyFromDefaultTransmission(transmissionKey);
  const modelQueryParam = createQueryParam('model', trimId);
  const trimQueryParam = createQueryParam('trim', selectedTransmissionKey);
  const yearQueryParam = createQueryParam('year', modelYear);
  const colorQueryParam = createQueryParam('color', defaultExteriorColorKey);

  const priceAndFinanceUrl = `${t(
    'Shared.Common.priceAndFinanceUrl',
  )}?vehicleType=${vehicleType}${yearQueryParam}${modelQueryParam}${trimQueryParam}${colorQueryParam}`;

  const trim = {
    trimKey,
    modelKey,
    trimId,
    transmissionId,
  };

  const financialFormatter = financials => productPricingFormatter(financials, trim);

  const {
    financial: productPrices,
    isFetching,
    hasError,
    setModels,
  } = useProductFinancial({
    vehicleType,
    formatter: financialFormatter,
  });

  const prices = getPricingWithLabel(productPrices, priceLabels);

  // display cta after prices is fetched and not empty
  const showPriceAndFinanceCTA = !isSticky && !hidePriceAndFinance;
  let discountValue;
  let hasDiscount;
  if (prices) {
    discountValue = prices?.discount?.priceDiscountAmount;
    hasDiscount = !!discountValue && !suppressDiscount;
  }

  useEffect(() => {
    setModels([{ modelKey, modelYear }]);
  }, []);

  useEffect(() => {
    setForceUpdate(value => !value);
  }, [hasDiscount]);

  const showLabel = !isSticky && !suppressDiscount && hasDiscount;

  return (
    <Box textAlign="center">
      <EqualHeightElement name="CompareCardCashSavings" placeholder={!showLabel}>
        <Optional when={showLabel}>
          <SaveLabel value={discountValue} saveStyles={priceComponentStyles.saveStyles} />
        </Optional>
      </EqualHeightElement>

      <ProductTitle title={productCardTitle} productIndex={productIndex} defaultHeader={H6} hasPadding={isSticky} />
      <ProductImageNoWidth thumbnail={thumbnail} productIndex={productIndex} isSticky={isSticky} />
      <EqualHeightElement name="CompareCardSpecialOfferBadge" placeholder={!hasSpecialOffer}>
        <Optional when={hasSpecialOffer}>
          <CardSpecialOfferBadge />
        </Optional>
      </EqualHeightElement>
      <Optional when={isVisibleOnMobile || !isSticky}>
        <PriceComponent
          allInPriceTooltipLabel={sellingPriceTooltipDiscalimerLabel}
          errorMessage={t('Pages.Models.Exploration.contactLocalDealerLabel')}
          hasError={hasError}
          height={['auto', 'auto', undefined]}
          horizontalAlignment="center"
          isFetching={isFetching}
          msrpStartingFromTooltipLabel={t('Shared.Common.msrpStartingFromTooltipLabel')}
          priceComponentStyles={priceComponentStyles}
          prices={prices}
          showSellingPriceDisclaimer={showSellingPriceDisclaimer}
          frieghtPDIFeesCost={payment?.frieghtPDIFeesCost}
        />
      </Optional>
      <Optional when={isDesktop ? !isSticky : isVisibleOnMobile || !isSticky}>
        <EqualHeightElement>
          <Optional when={showPriceAndFinanceCTA}>
            <PriceButtonWrapper {...wrapperGtmTags}>
              <PriceButton
                styling="primary"
                data-testid="cy-price-financial-cta"
                as="a"
                href={priceAndFinanceUrl}
                aria-label={t('Shared.Common.priceAndFinanceButton')}
                {...ctaGtmTags}
              >
                {t('Shared.Common.priceAndFinanceButton')}
              </PriceButton>
            </PriceButtonWrapper>
          </Optional>
          <Optional when={showBuyNowCta}>
            <PriceButtonWrapper>
              <BuyNowButton
                styling="special"
                data-testid={`cy-compare_products_buy_now_cta-${modelKey}`}
                onClick={() => {
                  if (!handleBuyNowClick) return;
                  handleBuyNowClick();
                }}
                {...ctaGtmTags}
              >
                {t('Shared.Common.buyNowButton')}
              </BuyNowButton>
            </PriceButtonWrapper>
          </Optional>
        </EqualHeightElement>
      </Optional>
    </Box>
  );
};

ProductPriceAndFinance.propTypes = {
  modelKey: PropTypes.string.isRequired,
  modelYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  trimId: PropTypes.string.isRequired,
  transmissionId: PropTypes.string.isRequired,
  hidePriceAndFinance: PropTypes.oneOf(['', '1']),
  hasSpecialOffer: PropTypes.bool,
};

export default ProductPriceAndFinance;
